import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import {
    MagnifyingGlassIcon,
    QuestionMarkCircleIcon,
    ChevronDownIcon,
    CheckCircleIcon,
    XCircleIcon
} from '@heroicons/react/24/outline'
import { faqAPI } from '../../services/api'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'

const FAQs = () => {
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedCategory, setSelectedCategory] = useState('')
    const [openIndex, setOpenIndex] = useState(0)

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['faqs', searchQuery, selectedCategory],
        queryFn: () => faqAPI.getFAQs({
            search: searchQuery,
            category: selectedCategory
        }),
    })

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? -1 : index)
    }

    const handleFeedback = async (faqId, isHelpful) => {
        try {
            if (isHelpful) {
                await faqAPI.markHelpful(faqId)
            } else {
                await faqAPI.markNotHelpful(faqId)
            }
        } catch (error) {
            console.error('Failed to submit feedback:', error)
        }
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <ErrorMessage message="Failed to load FAQs" onRetry={refetch} />
            </div>
        )
    }

    const faqs = data?.data?.faqs || []
    const categories = [
        { value: '', label: 'All Categories' },
        { value: 'general', label: 'General' },
        { value: 'admission', label: 'Admission' },
        { value: 'fees', label: 'Fees' },
        { value: 'courses', label: 'Courses' },
        { value: 'university', label: 'University' },
        { value: 'eligibility', label: 'Eligibility' },
        { value: 'career', label: 'Career' },
        { value: 'placement', label: 'Placement' },
    ]

    return (
        <>
            <Helmet>
                <title>Frequently Asked Questions | MyMargdarshak</title>
                <meta name="description" content="Find answers to common questions about online education, admissions, fees, and career guidance. Get help with your education journey." />
                <meta name="keywords" content="FAQ, frequently asked questions, online education, admission help, career guidance" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 flex flex-col pt-0">
                {/* Premium Light Theme Hero Section */}
                <section className="relative pt-28 lg:pt-36 pb-16 lg:pb-32 overflow-hidden bg-white border-b border-gray-100">
                    {/* Dynamic Mesh Gradient Background - Subtle for Light Theme */}
                    <div className="absolute inset-0 z-0">
                        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-primary-100 blur-[120px] animate-pulse"></div>
                        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-secondary-100 blur-[120px] animate-pulse" style={{ animationDelay: '2s' }}></div>
                        <div className="absolute top-[20%] right-[10%] w-[30%] h-[30%] rounded-full bg-accent-50 blur-[100px]"></div>
                    </div>

                    {/* Subtle Overlay Grid */}
                    <div className="absolute inset-0 z-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>

                    <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="max-w-4xl mx-auto text-center">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                                className="inline-flex items-center space-x-2 bg-primary-50 border border-primary-100 px-4 py-2 rounded-full mb-8"
                            >
                                <QuestionMarkCircleIcon className="w-5 h-5 text-primary-600" />
                                <span className="text-sm font-bold text-primary-700 uppercase tracking-widest">Help Center</span>
                            </motion.div>

                            <motion.h1 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="text-4xl sm:text-6xl lg:text-7xl font-black mb-6 text-slate-900 leading-tight tracking-tight"
                            >
                                How can we <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-accent-600">help you?</span>
                            </motion.h1>

                            <motion.p 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="text-lg sm:text-xl text-slate-600 mb-12 max-w-2xl mx-auto font-medium"
                            >
                                Search our knowledge base or browse frequently asked questions below to find the answers you need.
                            </motion.p>

                            {/* Redesigned Search Bar - Light Theme Glassmorphism */}
                            <motion.div 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                                className="max-w-2xl mx-auto"
                            >
                                <div className="relative group">
                                    <div className="absolute -inset-1 bg-gradient-to-r from-primary-400 to-accent-400 rounded-2xl blur opacity-20 group-focus-within:opacity-40 transition duration-1000"></div>
                                    <div className="relative flex flex-col md:flex-row gap-4 bg-white/80 backdrop-blur-xl border border-white p-2 rounded-2xl shadow-xl shadow-slate-200/50">
                                        <div className="relative flex-1">
                                            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-400 group-focus-within:text-primary-600 transition-colors" />
                                            <input
                                                type="text"
                                                placeholder="Ask a question..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                className="w-full pl-12 pr-4 py-4 bg-transparent text-slate-900 border-none focus:ring-0 text-lg placeholder:text-slate-400"
                                            />
                                        </div>
                                        <div className="md:w-px md:h-10 bg-slate-200 my-auto hidden md:block"></div>
                                        <select
                                            value={selectedCategory}
                                            onChange={(e) => setSelectedCategory(e.target.value)}
                                            className="px-6 py-4 bg-transparent text-slate-700 border-none focus:ring-0 text-base md:min-w-[180px] rounded-xl cursor-pointer"
                                        >
                                            {categories.map((category) => (
                                                <option key={category.value} value={category.value} className="bg-white text-slate-900">
                                                    {category.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* FAQs Section */}
                <section className="py-12">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        {isLoading ? (
                            <div className="flex justify-center py-12">
                                <LoadingSpinner size="lg" />
                            </div>
                        ) : (
                            <>
                                {faqs.length > 0 ? (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6 }}
                                        className="space-y-4"
                                    >
                                        {faqs.map((faq, index) => (
                                            <motion.div
                                                key={faq.id}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                                className="card overflow-hidden"
                                            >
                                                <button
                                                    onClick={() => toggleFAQ(index)}
                                                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                                                >
                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-semibold text-gray-900 pr-4">
                                                            {faq.question}
                                                        </h3>
                                                        {faq.category && (
                                                            <span className="inline-block mt-2 px-2 py-1 bg-primary-100 text-primary-800 text-xs font-medium rounded-full">
                                                                {faq.category}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <motion.div
                                                        animate={{ rotate: openIndex === index ? 180 : 0 }}
                                                        transition={{ duration: 0.2 }}
                                                        className="flex-shrink-0"
                                                    >
                                                        <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                                                    </motion.div>
                                                </button>

                                                <AnimatePresence>
                                                    {openIndex === index && (
                                                        <motion.div
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: 'auto', opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                                                            className="overflow-hidden"
                                                        >
                                                            <div className="px-6 pb-4">
                                                                <div className="w-full h-px bg-gray-200 mb-4"></div>
                                                                <p className="text-gray-600 leading-relaxed mb-4">
                                                                    {faq.answer}
                                                                </p>

                                                                {/* Feedback Section */}
                                                                {/* <div className="flex items-center space-x-4 pt-4 border-t border-gray-100">
                                                                    <span className="text-sm text-gray-500">Was this helpful?</span>
                                                                    <div className="flex items-center space-x-2">
                                                                        <button
                                                                            onClick={() => handleFeedback(faq.id, true)}
                                                                            className="flex items-center space-x-1 text-green-600 hover:text-green-700 text-sm font-medium"
                                                                        >
                                                                            <CheckCircleIcon className="w-4 h-4" />
                                                                            <span>Yes</span>
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleFeedback(faq.id, false)}
                                                                            className="flex items-center space-x-1 text-red-600 hover:text-red-700 text-sm font-medium"
                                                                        >
                                                                            <XCircleIcon className="w-4 h-4" />
                                                                            <span>No</span>
                                                                        </button>
                                                                    </div>
                                                                </div> */}
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </motion.div>
                                        ))}
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6 }}
                                        className="text-center py-12"
                                    >
                                        <QuestionMarkCircleIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No FAQs Found</h3>
                                        <p className="text-gray-600">
                                            {searchQuery || selectedCategory
                                                ? 'Try adjusting your search or filter criteria.'
                                                : 'No FAQs are available at the moment.'
                                            }
                                        </p>
                                    </motion.div>
                                )}
                            </>
                        )}
                    </div>
                </section>

                {/* Contact Section */}
                <section className="py-16 bg-white">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2 className="text-3xl font-bold text-gray-900 mb-4">
                                Still have questions?
                            </h2>
                            <p className="text-xl text-gray-600 mb-8">
                                Can't find the answer you're looking for? Our support team is here to help.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <a
                                    href="/contact"
                                    className="btn-primary btn-lg"
                                >
                                    Contact Support
                                </a>
                                <button className="btn-outline btn-lg">
                                    Get Free Consultation
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </section>
            </div>
        </>
    )
}

export default FAQs
